import type { GetSfAccountsRequest } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import {
    accountOptions,
    nonEndemicAccountOptions,
} from "../../config/testSite/sfAccountOptions";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { QueryParam } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

interface Account {
    value: string;
    label: string;
    tag?: string;
    test?: boolean;
}

export function useSfAccountsAction(): [
    AsyncState<Account[] | undefined>,
    UseAsyncActions<Account[]>,
] {
    const request: GetSfAccountsRequest = {};

    const [asyncSfAccountsState, asyncSfAccountsAction] = useAsync(async () => {
        const accounts: Account[] = [];

        if (FeatureFlagsStore.getState().enableTestAccounts) {
            // Convert test site accounts to the expected format
            const endemicAccounts = [...accountOptions]
                .filter(
                    (account) =>
                        account[QueryParam.SALESFORCE_ID] &&
                        account[QueryParam.SALESFORCE_NAME],
                )
                .map(
                    (account): Account => ({
                        value: account[QueryParam.SALESFORCE_ID]!,
                        label: `${account[QueryParam.SALESFORCE_NAME]} | ${account[QueryParam.SALESFORCE_ID]}`,
                        test: true,
                        tag: "Endemic",
                    }),
                );

            const nonEndemicAccounts = [...nonEndemicAccountOptions]
                .filter(
                    (account) =>
                        account[QueryParam.SALESFORCE_ID] &&
                        account[QueryParam.SALESFORCE_NAME],
                )
                .map(
                    (account): Account => ({
                        value: account[QueryParam.SALESFORCE_ID]!,
                        label: `${account[QueryParam.SALESFORCE_NAME]} | ${account[QueryParam.SALESFORCE_ID]}`,
                        test: true,
                        tag: "Non-Endemic",
                    }),
                );

            accounts.push(...endemicAccounts, ...nonEndemicAccounts);
        }

        //ORIGINAL API CALL (comment out for testing)

        const client = await getCdbApiClient();
        const requesterAlias = authStore.getState().username;
        const response = await client.getSfAccounts({
            ...request,
            requesterAlias,
        });

        if (!response?.data) {
            return accounts;
        }

        // Ensure we only create accounts with valid advertiserIds
        const validSfAccounts = response.data
            .filter(
                (
                    account,
                ): account is { advertiserId: string; advertiser: string } =>
                    typeof account.advertiserId === "string" &&
                    typeof account.advertiser === "string",
            )
            .map(
                (account): Account => ({
                    value: account.advertiserId,
                    label: `${account.advertiser} | ${account.advertiserId}`,
                }),
            );

        accounts.push(...validSfAccounts);

        return accounts;
    });

    useAsyncErrorStatusNotification(asyncSfAccountsState);

    return [asyncSfAccountsState, asyncSfAccountsAction];
}
